/*
 * Newsletter popup timing and dismissal.
 *
 * Deliberately does NOT pause anything behind it: the backdrop blurs the
 * page via CSS, so the homepage video carries on playing underneath.
 */
(function () {
  const DISMISSED_KEY = 'molangela:newsletter-dismissed-until';
  const SUBSCRIBED_KEY = 'molangela:newsletter-subscribed';

  // Private browsing and blocked site data both make storage throw.
  // Losing the memory is acceptable; breaking the page is not.
  function readStore(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeStore(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      /* no-op */
    }
  }

  class NewsletterPopup extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.newsletter-popup__panel');
      this.closeButton = this.querySelector('[data-close]');
      this.backdrop = this.querySelector('[data-backdrop]');
      this.emailInput = this.querySelector('input[type="email"]');

      this.style.setProperty('--newsletter-blur', (this.dataset.blur || 12) + 'px');

      // Shopify reloads the page after the form posts, so a success
      // message present on load means this visitor just subscribed.
      if (this.querySelector('[data-success]')) {
        writeStore(SUBSCRIBED_KEY, '1');
        this.setupCopy();
        this.open();
        return;
      }

      // Re-open on a validation error so the message is not lost.
      if (this.querySelector('.newsletter-popup__error')) {
        this.open();
        return;
      }

      if (!this.shouldShow()) return;

      const delay = (parseInt(this.dataset.delay, 10) || 0) * 1000;
      this.timer = setTimeout(() => this.open(), delay);
    }

    setupCopy() {
      const button = this.querySelector('[data-copy]');
      const code = this.querySelector('[data-code]');
      if (!button || !code) return;

      const original = button.textContent;
      button.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(code.textContent.trim());
          button.textContent = 'copied';
        } catch (error) {
          // Clipboard needs permission and a secure context; selecting the
          // text is a reasonable fallback so the code stays obtainable.
          const range = document.createRange();
          range.selectNodeContents(code);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
          button.textContent = 'select & copy';
        }
        setTimeout(() => {
          button.textContent = original;
        }, 2500);
      });
    }

    shouldShow() {
      if (readStore(SUBSCRIBED_KEY)) return false;

      const until = parseInt(readStore(DISMISSED_KEY), 10);
      if (until && Date.now() < until) return false;

      return true;
    }

    open() {
      this.hidden = false;
      this.lastFocused = document.activeElement;

      this.onKeydown = (event) => {
        if (event.key === 'Escape') this.dismiss();
      };
      document.addEventListener('keydown', this.onKeydown);
      this.closeButton.addEventListener('click', () => this.dismiss());
      this.backdrop.addEventListener('click', () => this.dismiss());

      // Focus the field rather than the dialog so a keyboard visitor can
      // type straight away; on touch this would force the keyboard open,
      // so it is skipped there.
      const isTouch = window.matchMedia('(hover: none)').matches;
      if (this.emailInput && !isTouch) {
        this.emailInput.focus();
      } else {
        this.closeButton.focus();
      }

      this.dispatchEvent(new CustomEvent('newsletter-popup:open', { bubbles: true }));
    }

    dismiss() {
      const days = parseInt(this.dataset.remindDays, 10) || 30;
      writeStore(DISMISSED_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));

      this.hidden = true;
      document.removeEventListener('keydown', this.onKeydown);
      if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus();

      this.dispatchEvent(new CustomEvent('newsletter-popup:dismiss', { bubbles: true }));
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('keydown', this.onKeydown);
    }
  }

  if (!customElements.get('newsletter-popup')) {
    customElements.define('newsletter-popup', NewsletterPopup);
  }
})();
